import { useMemo, useState } from 'react';
import { Alert, Button, Radio, Space, Typography, App } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { trpc } from '../../../../lib/api';
import PlanChangePreviewDialog from '@/components/subscription/PlanChangePreviewDialog';
import type { PlanChangePreview } from '@/types/subscription';
import type { Company } from '../../../../types/company';
import type { SubscriptionContractSummary } from '../types';
import { withDevModalFilePath } from '../../../../components/common';

const { Modal } = withDevModalFilePath('client/src/pages/admin/companies/hooks/usePlanChange.tsx');

const { Text, Paragraph } = Typography;

export interface UsePlanChangeOptions {
  companyId: string;
  company: Company | undefined;
  manageablePlanCodes: readonly string[];
  planDescriptions: Record<string, string>;
  subscriptionPlanLabels: Record<string, string>;
  planChangeButtonDisabled?: boolean;
  onPlanChangeSuccess?: () => void;
}

export interface UsePlanChangeResult {
  planChangeButton: React.ReactNode;
  planChangeModals: React.ReactNode;
}

export const usePlanChange = ({
  companyId,
  company,
  manageablePlanCodes,
  planDescriptions,
  subscriptionPlanLabels,
  planChangeButtonDisabled,
  onPlanChangeSuccess,
}: UsePlanChangeOptions): UsePlanChangeResult => {
  const { message } = App.useApp();
  const [isPlanChangeModalOpen, setIsPlanChangeModalOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [selectedPlanCode, setSelectedPlanCode] = useState<string | null>(null);
  const [planChangePreview, setPlanChangePreview] = useState<PlanChangePreview | null>(null);
  const [planChangeError, setPlanChangeError] = useState<string | null>(null);

  const utils = trpc.useContext();

  const {
    data: contracts,
    isLoading: isContractsLoading,
    refetch: refetchContracts,
  } = trpc.subscriptionContractAdmin.list.useQuery(
    company?.name ? { search: company.name } : undefined,
    {
      enabled: !!company?.id,
      refetchOnWindowFocus: false,
    },
  );

  const planChangeContract = useMemo<SubscriptionContractSummary | null>(() => {
    if (!company || !contracts?.length) {
      return null;
    }
    const found = (contracts as SubscriptionContractSummary[]).find(
      (contract) => contract.companyId === company.id && contract.stripeSubscriptionId,
    );
    return found ?? null;
  }, [company, contracts]);

  const previewPlanQuery = trpc.subscriptionContractAdmin.previewPlanChange.useQuery(
    planChangeContract && selectedPlanCode
      ? {
          subscriptionId: planChangeContract.stripeSubscriptionId as string,
          newPlanCode: selectedPlanCode,
        }
      : undefined,
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  const changePlanMutation = trpc.subscriptionContractAdmin.changePlan.useMutation();

  const currentPlanCode = planChangeContract?.planCode ?? company?.subscriptionPlan ?? null;
  const previewLoading = previewPlanQuery.isFetching;
  const changePlanLoading = changePlanMutation.isPending;
  const canPreview =
    !!planChangeContract && !!selectedPlanCode && selectedPlanCode !== planChangeContract.planCode;
  const previewErrorFallback = 'プラン変更プレビューの取得に失敗しました。';
  const confirmErrorFallback = 'プラン変更に失敗しました。';

  const resetPreviewState = () => {
    setPlanChangePreview(null);
    setPlanChangeError(null);
  };

  const resolveErrorMessage = (error: unknown, fallback: string) => {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'object' && error !== null && 'message' in error) {
      const messageValue = (error as { message?: unknown }).message;
      if (typeof messageValue === 'string') {
        return messageValue;
      }
    }
    return fallback;
  };

  const handleOpenModal = () => {
    if (!planChangeContract) {
      message.warning('Stripeサブスクリプションが見つからないためプラン変更できません。');
      return;
    }
    setSelectedPlanCode(planChangeContract.planCode);
    resetPreviewState();
    setIsPreviewDialogOpen(false);
    setIsPlanChangeModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsPlanChangeModalOpen(false);
    setIsPreviewDialogOpen(false);
    resetPreviewState();
    setSelectedPlanCode(null);
  };

  const handlePlanSelectionChange = (event: RadioChangeEvent) => {
    setSelectedPlanCode(event.target.value);
    setPlanChangeError(null);
    setPlanChangePreview(null);
  };

  const handleRequestPreview = async () => {
    if (!planChangeContract) {
      message.error('プラン変更対象の契約が見つかりません。');
      return;
    }
    if (!selectedPlanCode) {
      message.warning('新しいプランを選択してください。');
      return;
    }
    if (selectedPlanCode === planChangeContract.planCode) {
      message.warning('現在と異なるプランを選択してください。');
      return;
    }
    setPlanChangeError(null);
    setPlanChangePreview(null);
    try {
      const result = await previewPlanQuery.refetch();
      if (result.error) {
        throw result.error;
      }
      if (!result.data) {
        message.info('プレビューの取得に失敗しました。時間をおいて再度お試しください。');
        return;
      }
      setPlanChangePreview(result.data as PlanChangePreview);
      setIsPreviewDialogOpen(true);
    } catch (error: unknown) {
      const description = resolveErrorMessage(error, previewErrorFallback);
      setPlanChangeError(description);
      message.error(description);
    }
  };

  const handleRetryPreview = () => {
    void handleRequestPreview();
  };

  const handleConfirmPlanChange = async () => {
    if (!planChangeContract || !selectedPlanCode) {
      message.warning('契約またはプランが未選択です。');
      return;
    }
    const previousCompany = utils?.company?.getById?.getData?.(companyId ?? '');
    const previousContracts = utils && company?.name
      ? utils.subscriptionContractAdmin?.list?.getData?.({ search: company.name })
      : null;

    // Optimistic UI更新はchangeTypeに応じて分岐
    const isDowngrade = planChangePreview?.changeType === 'downgrade';

    if (!isDowngrade) {
      // アップグレードの場合のみ即座にplanCodeを更新
      utils?.company?.getById?.setData?.(companyId ?? '', (old) =>
        old
          ? {
              ...old,
              subscriptionPlan: selectedPlanCode,
              updatedAt: new Date().toISOString(),
            }
          : old,
      );

      if (company?.name) {
        utils?.subscriptionContractAdmin?.list?.setData?.({ search: company.name }, (oldContracts) => {
          if (!oldContracts) {
            return oldContracts;
          }
          return (oldContracts as SubscriptionContractSummary[]).map((contract) =>
            contract.contractId === planChangeContract.contractId
              ? { ...contract, planCode: selectedPlanCode }
              : contract,
          );
        });
      }
    } else {
      // ダウングレードの場合はscheduledPlanCodeを設定（planCodeは維持）
      if (company?.name) {
        utils?.subscriptionContractAdmin?.list?.setData?.({ search: company.name }, (oldContracts) => {
          if (!oldContracts) {
            return oldContracts;
          }
          return (oldContracts as SubscriptionContractSummary[]).map((contract) =>
            contract.contractId === planChangeContract.contractId
              ? {
                  ...contract,
                  scheduledPlanCode: selectedPlanCode,
                  scheduledPlanEffectiveDate: planChangePreview?.effectiveDate ?? null,
                }
              : contract,
          );
        });
      }
    }

    try {
      const result = await changePlanMutation.mutateAsync({
        contractId: planChangeContract.contractId,
        planCode: selectedPlanCode,
      });

      // changeTypeに応じてメッセージを分岐
      if (result.changeType === 'downgrade') {
        const effectiveDate = planChangePreview?.effectiveDate
          ? new Date(planChangePreview.effectiveDate).toLocaleDateString('ja-JP')
          : '次回請求日';
        message.success(`プラン変更を予約しました。${effectiveDate}から適用されます。`);
      } else {
        message.success('プランを変更しました。');
      }
      setIsPreviewDialogOpen(false);
      setIsPlanChangeModalOpen(false);
      setSelectedPlanCode(null);
      resetPreviewState();
      const tasks: Array<Promise<unknown>> = [];
      const invalidateCompany = utils?.company?.getById?.invalidate;
      if (invalidateCompany) {
        tasks.push(invalidateCompany(companyId ?? ''));
      }
      tasks.push(refetchContracts());
      await Promise.allSettled(tasks);
      onPlanChangeSuccess?.();
    } catch (error: unknown) {
      console.error('[usePlanChange] プラン変更処理でエラーが発生しました', error);
      if (previousCompany) {
        utils?.company?.getById?.setData?.(companyId ?? '', previousCompany);
      }
      if (previousContracts && company?.name) {
        utils?.subscriptionContractAdmin?.list?.setData?.({ search: company.name }, previousContracts);
      }
      const description = resolveErrorMessage(error, confirmErrorFallback);
      setPlanChangeError(description);
      message.error(description);
    }
  };

  const planChangeButton =
    planChangeContract && company ? (
      <Button
        type="primary"
        icon={<SwapOutlined />}
        onClick={handleOpenModal}
        disabled={planChangeButtonDisabled || isContractsLoading}
      >
        プラン変更
      </Button>
    ) : null;

  const planChangeModals = (
    <>
      <Modal
        title="プランを選択"
        open={isPlanChangeModalOpen}
        onCancel={handleCloseModal}
        footer={[
          <Button key="cancel" onClick={handleCloseModal}>
            キャンセル
          </Button>,
          <Button
            key="preview"
            type="primary"
            onClick={handleRequestPreview}
            loading={previewLoading}
            disabled={!canPreview || previewLoading}
          >
            プレビュー表示
          </Button>,
        ]}
        destroyOnHidden={false}
        maskClosable={false}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {currentPlanCode && (
            <Alert
              type="info"
              showIcon
              message={`現在のプラン: ${subscriptionPlanLabels[currentPlanCode] ?? currentPlanCode}`}
            />
          )}
          <Paragraph style={{ marginBottom: 0 }}>
            新しいプランを選択し、「プレビュー表示」で差額請求や適用日を確認してください。
          </Paragraph>
          <Radio.Group
            onChange={handlePlanSelectionChange}
            value={selectedPlanCode ?? currentPlanCode ?? undefined}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {manageablePlanCodes.map((planCode) => (
                <Radio key={planCode} value={planCode} disabled={planCode === planChangeContract?.planCode}>
                  <Space direction="vertical" size={0}>
                    <Text strong>{subscriptionPlanLabels[planCode] ?? planCode}</Text>
                    <Text type="secondary">
                      {planDescriptions[planCode] ?? '詳細情報は準備中です'}
                    </Text>
                  </Space>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
          {planChangeError && (
            <Alert
              type="error"
              showIcon
              message="プレビューの取得に失敗しました"
              description={
                <Space direction="vertical" size={4}>
                  <Text>{planChangeError}</Text>
                  <Button type="link" size="small" onClick={handleRetryPreview}>
                    リトライ
                  </Button>
                </Space>
              }
            />
          )}
        </Space>
      </Modal>

      <PlanChangePreviewDialog
        open={isPreviewDialogOpen}
        preview={planChangePreview}
        onCancel={() => setIsPreviewDialogOpen(false)}
        onConfirm={handleConfirmPlanChange}
        confirmLoading={changePlanLoading}
        descriptionMap={subscriptionPlanLabels}
      />
    </>
  );

  return {
    planChangeButton,
    planChangeModals,
  };
};

